import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";
import { cn } from "./cn";

// shadcn/ui button on the mock's .btn: 36 px high, 7 px radius, 14 px semibold.
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-sm border text-base font-semibold whitespace-nowrap no-underline transition-colors hover:no-underline disabled:pointer-events-none disabled:opacity-45 aria-disabled:pointer-events-none aria-disabled:opacity-45 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "border-primary bg-primary text-primary-ink hover:border-primary-hover hover:bg-primary-hover",
        outline: "border-line-strong bg-surface text-ink hover:bg-hover",
        ghost: "border-transparent bg-transparent text-ink-2 hover:bg-hover hover:text-ink",
        destructive: "border-line-strong bg-surface text-crit-ink hover:bg-crit-soft",
        link: "h-auto border-transparent px-0 text-link underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-3.5",
        sm: "h-[30px] gap-1.5 px-2.5 text-sm",
        lg: "h-10 px-4",
        icon: "size-9",
        "icon-sm": "size-[30px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    /** Renders the child (a link, for example) with the button's look. */
    asChild?: boolean;
  };

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  type,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      type={asChild ? type : (type ?? "button")}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, type ButtonProps, buttonVariants };
